import { useState, useRef, useEffect } from 'react'

import { useProcedureListTableLogic } from 'components/ProcedureList/ProcedureListTable.logic'
import { useGetSurgeriesQuery } from 'lib/apollo/hooks'
import { useDisablePolling } from 'lib/useDisablePolling'
import dayjs from 'lib/dayjs'

import { ItemResponse } from 'common/types'
import { extractTrayJSONData } from 'lib/utils/extractTrayJSONData'
import { useApolloClients } from 'lib/apollo/apolloProvider'

const initialBefore = dayjs().endOf('day').toDate()
const initialAfter = dayjs().startOf('day').toDate()

export const useListOfSurgeries = ({
  traySurgeryId,
}: {
  traySurgeryId: string
}) => {
  const disablePolling = useDisablePolling()
  const [beforeDate, setBeforeDate] = useState(initialBefore)
  const [afterDate, setAfterDate] = useState(initialAfter)

  const { servicesClient: client } = useApolloClients()

  const { loading, error, data, refetch } = useGetSurgeriesQuery(
    {
      before: beforeDate,
      after: afterDate,
      limit: 0,
    },
    {
      client,
      disablePolling,
    }
  )
  const surgeries = data && data.getSurgeries?.results

  const { isNurse, caseInformation, sortedSurgeries } =
    useProcedureListTableLogic({
      events: surgeries,
    })

  const handleBackDay = () => {
    setBeforeDate(dayjs(beforeDate).subtract(1, 'day').toDate())
    setAfterDate(dayjs(afterDate).subtract(1, 'day').toDate())
  }

  const handleForwardDay = () => {
    setBeforeDate(dayjs(beforeDate).add(1, 'day').toDate())
    setAfterDate(dayjs(afterDate).add(1, 'day').toDate())
  }

  const isSurgerySelected = (surgeryId: string, trayData: ItemResponse) => {
    const previousProductDetails = extractTrayJSONData(trayData)
    const selectedSurgeryId = traySurgeryId || previousProductDetails?.surgeryId
    return surgeryId === selectedSurgeryId
  }

  // UI/scroll/accordion logic
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrolledToBottom, setScrolledToBottom] = useState(false)
  const [expanded, setExpanded] = useState<string | false>(false)
  const handleAccordionToggle =
    (hospitalName: string) =>
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? hospitalName : false)
    }

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const handleScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 2
      setScrolledToBottom(atBottom)
    }
    el.addEventListener('scroll', handleScroll)
    handleScroll()
    return () => el.removeEventListener('scroll', handleScroll)
  }, [])

  return {
    loading,
    sortedSurgeries,
    caseInformation,
    isNurse,
    handleBackDay,
    handleForwardDay,
    isSurgerySelected,
    beforeDate,
    afterDate,
    error,
    refetch,
    // UI/scroll/accordion
    containerRef,
    scrolledToBottom,
    expanded,
    setExpanded,
    handleAccordionToggle,
  }
}
